import { useQuery } from '@tanstack/react-query';
import type { PatientSearchResult } from '@wodi/shared';
import { Button, Select } from '@wodi/ui';
import { useState } from 'react';

import { patientVisits } from '../api/patients.ts';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import { dateOfText } from './format.tsx';
import styles from './operations.module.css';

// Linking a patient, and optionally one of their visits, to an operational record
// (docs/plans/phase3-operations-modules.md §0.2). The search term stays in this component;
// only the chosen patient's ID leaves it.

export interface LinkedPatient {
  id: string;
  displayName: string;
  patientNumber: string;
}

const NO_VISIT = 'none';

export function PatientLink({
  patient,
  visitId,
  onChange,
  withVisit = true,
}: {
  patient: LinkedPatient | null;
  visitId: string | null;
  onChange: (patient: LinkedPatient | null, visitId: string | null) => void;
  withVisit?: boolean;
}) {
  const [text, setText] = useState('');
  const visits = useQuery({
    queryKey: ['patients', patient?.id ?? 'none', 'visits', 'link'],
    queryFn: () => patientVisits(patient?.id ?? '', undefined),
    enabled: patient !== null && withVisit,
  });
  if (patient === null) {
    return (
      <PatientSearchPicker
        inputValue={text}
        onInputChange={setText}
        onSelect={(result: PatientSearchResult) => {
          setText('');
          onChange(
            {
              id: result.id,
              displayName: result.displayName,
              patientNumber: result.patientNumber,
            },
            null,
          );
        }}
      />
    );
  }
  return (
    <div className={styles.form}>
      <div className={styles.row}>
        <p className={styles.prose}>
          Patient: {patient.displayName} <span className="wodi-id">{patient.patientNumber}</span>
        </p>
        <Button
          dense
          onPress={() => {
            onChange(null, null);
          }}
        >
          Change patient
        </Button>
      </div>
      {withVisit ? (
        <Select
          label="Visit (optional)"
          description="The patient's latest visits."
          options={[
            { id: NO_VISIT, label: 'No visit' },
            ...(visits.data?.items ?? []).map((v) => ({
              id: v.id,
              label: `${v.visitNumber}, ${dateOfText(v.startedAt)}`,
            })),
          ]}
          value={visitId ?? NO_VISIT}
          onChange={(key) => {
            const id = key === null ? NO_VISIT : String(key);
            onChange(patient, id === NO_VISIT ? null : id);
          }}
          {...(visits.isError ? { errorMessage: "Visits couldn't be loaded." } : {})}
        />
      ) : null}
    </div>
  );
}
